import { Dialog } from '@headlessui/react';
import { Check, Globe } from 'lucide-react';
import { useState } from 'react';

interface CountrySelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (countryCode: string) => void;
}

// Countries supported by DevotionFund: US + EU/EEA countries only
// DevotionFund uses two Stripe accounts:
// - US Stripe account for US creators (USD)
// - EU Stripe account for EU/EEA creators (EUR)
const STRIPE_COUNTRIES = [
    // United States
    { code: 'US', name: '🇺🇸 United States', region: 'US' },

    // EU/EEA Countries
    { code: 'AT', name: '🇦🇹 Austria', region: 'EU' },
    { code: 'BE', name: '🇧🇪 Belgium', region: 'EU' },
    { code: 'BG', name: '🇧🇬 Bulgaria', region: 'EU' },
    { code: 'HR', name: '🇭🇷 Croatia', region: 'EU' },
    { code: 'CY', name: '🇨🇾 Cyprus', region: 'EU' },
    { code: 'CZ', name: '🇨🇿 Czech Republic', region: 'EU' },
    { code: 'DK', name: '🇩🇰 Denmark', region: 'EU' },
    { code: 'EE', name: '🇪🇪 Estonia', region: 'EU' },
    { code: 'FI', name: '🇫🇮 Finland', region: 'EU' },
    { code: 'FR', name: '🇫🇷 France', region: 'EU' },
    { code: 'DE', name: '🇩🇪 Germany', region: 'EU' },
    { code: 'GR', name: '🇬🇷 Greece', region: 'EU' },
    { code: 'HU', name: '🇭🇺 Hungary', region: 'EU' },
    { code: 'IE', name: '🇮🇪 Ireland', region: 'EU' },
    { code: 'IT', name: '🇮🇹 Italy', region: 'EU' },
    { code: 'LV', name: '🇱🇻 Latvia', region: 'EU' },
    { code: 'LT', name: '🇱🇹 Lithuania', region: 'EU' },
    { code: 'LU', name: '🇱🇺 Luxembourg', region: 'EU' },
    { code: 'MT', name: '🇲🇹 Malta', region: 'EU' },
    { code: 'NL', name: '🇳🇱 Netherlands', region: 'EU' },
    { code: 'PL', name: '🇵🇱 Poland', region: 'EU' },
    { code: 'PT', name: '🇵🇹 Portugal', region: 'EU' },
    { code: 'RO', name: '🇷🇴 Romania', region: 'EU' },
    { code: 'SK', name: '🇸🇰 Slovakia', region: 'EU' },
    { code: 'SI', name: '🇸🇮 Slovenia', region: 'EU' },
    { code: 'ES', name: '🇪🇸 Spain', region: 'EU' },
    { code: 'SE', name: '🇸🇪 Sweden', region: 'EU' },
].sort((a, b) => a.name.localeCompare(b.name));

export default function CountrySelectionModal({ isOpen, onClose, onSelect }: CountrySelectionModalProps) {
    const [selectedCountry, setSelectedCountry] = useState('US');
    const [searchQuery, setSearchQuery] = useState('');

    const filteredCountries = STRIPE_COUNTRIES.filter(country =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSubmit = () => {
        onSelect(selectedCountry);
        onClose();
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-[110]">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="mx-auto max-w-md w-full rounded-2xl bg-white p-6 shadow-xl">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <Globe className="w-6 h-6 text-blue-600" />
                        </div>
                        <Dialog.Title className="text-xl font-semibold text-gray-900">
                            Select your country
                        </Dialog.Title>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                        Please select the country where you have your bank account and identification documents.
                        DevotionFund supports creators in the United States (USD) and European Union countries (EUR).
                    </p>

                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search countries..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                    </div>

                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg mb-4">
                        {filteredCountries.map((country) => (
                            <button
                                key={country.code}
                                onClick={() => setSelectedCountry(country.code)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 active:bg-gray-100 active:scale-[0.99] transition-colors flex items-center justify-between ${selectedCountry === country.code ? 'bg-primary/10 border-l-4 border-primary' : ''
                                    }`}
                            >
                                <span className="text-sm font-medium text-gray-900">{country.name}</span>
                                <span className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{country.code}</span>
                                    {selectedCountry === country.code && (
                                        <Check className="w-4 h-4 text-primary" />
                                    )}
                                </span>
                            </button>
                        ))}
                        {filteredCountries.length === 0 && (
                            <div className="px-4 py-8 text-center text-sm text-gray-500">
                                No countries found matching "{searchQuery}"
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                        >
                            Continue
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}

